import { EyeOutlined } from "@ant-design/icons";
import { Button, Dropdown, Input } from "antd";
import { ColumnsType } from "antd/es/table";
import { useEffect, useRef } from "react";
import { Link } from "react-router-dom";
import { useAppSelector } from "../../../app/store";
import ActionThreeDotIcon from "../../../assets/icons/ActionThreeDotIcon";
import SearchIcon from "../../../assets/icons/SearchIcon";
import CustomTable from "../../../components/ui/CustomTable";
import { useStatsByUsersMutation } from "../../../features/cashCollection/cashCollectionSetup/cashCollectionSetupApi";
import { useGetUserListQuery } from "../../../features/portal/Products/productsApi";
import useError from "../../../hooks/useError";
import useTable from "../../../hooks/useTable";
import SidebarLayout from "../../../layouts/SidebarLayout";
import { GetUserListParams } from "../../../types/portal/product";
import { countParPageSerialNumber } from "../../../utils/number";
import { capitalize } from "../../../utils/string";
import { addKeyInArray, formatNumber } from "../../../utils/utils";

export default function FieldOfficersList() {
  const { page, limit, handlePagination, handleLimit, handleSearch, keyword } =
    useTable<GetUserListParams>();

  const prevUserIdsRef = useRef<string[] | number[] | null>(null);

  const projectId = localStorage.getItem("product_id");
  const { user } = useAppSelector((state) => state.auth);

  const {
    isError: getAllUserIsError,
    error: getAllUserError,
    isLoading: getAllUserIsLoading,
    data: getAllUserData,
    isFetching: getAllUserIsFatching,
  } = useGetUserListQuery(
    {
      search: keyword,
      id: projectId, // ✅ move id here
      page,
      limit,
      companyId: user?.data?.company_id, // ✅ match query definition
      roleId: undefined, // if you don’t have it yet
    },
    {
      skip: !user?.data?.company_id || !projectId, // ✅ skip if missing
    }
  );

  const [
    statsByUsers,
    {
      data: statsByUsersData,
      isLoading: statsByUsersIsLoading,
      isError: statsByUsersIsError,
      error: statsByUsersError,
    },
  ] = useStatsByUsersMutation();

  const userIds = getAllUserData?.data?.users?.map((user) => user.userId) || [];

  const mergedUsers = getAllUserData?.data?.users?.map((user: any) => {
    const match = statsByUsersData?.data?.find(
      (stat: any) => stat.userId === user.user.id
    );

    return {
      ...user,

      totalCollection: match?.totalCollection ?? 0,
      totalDeposit: match?.totalDeposit ?? 0,
      totalDue: match?.totalDue ?? 0,
      totalRows: match?.totalRows ?? 0,
    };
  });

  useError(getAllUserIsError, getAllUserError, "Get all users fail");
  useError(statsByUsersIsError, statsByUsersError, "Stats by users data fail");

  useEffect(() => {
    if (!userIds || userIds.length === 0) return;

    // compare new userIds with previous userIds
    const isSame =
      prevUserIdsRef.current &&
      JSON.stringify(prevUserIdsRef.current) === JSON.stringify(userIds);

    if (!isSame) {
      // update previous value
      prevUserIdsRef.current = userIds;

      // call API only ONCE for this new userIds
      statsByUsers({ userIds });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userIds]);

  const columns: ColumnsType<any> = [
    {
      title: "#SL",
      align: "center",
      width: 60,
      render: (_, __, index) => countParPageSerialNumber(page, limit, index),
    },
    {
      title: <div className="text-nowrap">Employee ID</div>,
      dataIndex: "userId",
      align: "center",
      render: (val) => {
        return val ? val : "N/A";
      },
    },
    {
      title: <div className="text-nowrap">Name</div>,
      dataIndex: "user",
      align: "center",
      render: (val) => {
        return val?.username ? capitalize(val?.username) : "N/A";
      },
    },
    {
      title: <div className="text-nowrap">Assigned Route</div>,
      dataIndex: "totalRows",
      align: "center",
      render: (val) => {
        return val ? val : "0";
      },
    },

    {
      title: <div className="text-nowrap">Collection (BDT)</div>,
      dataIndex: "totalCollection",
      align: "center",
      render: (val) => {
        return val ? formatNumber(val) : "0";
      },
    },
    {
      title: <div className="text-nowrap">Deposit (BDT)</div>,
      dataIndex: "totalDeposit",
      align: "center",
      render: (val) => {
        return val ? formatNumber(val) : "0";
      },
    },

    {
      title: <div className="text-nowrap">Remaining (BDT)</div>,
      dataIndex: "totalDue",
      align: "center",
      render: (val) => {
        return val ? formatNumber(val) : "0";
      },
    },

    {
      title: "Actions",
      dataIndex: "id",
      align: "center",
      render: (val: number, record: any) => {
        const items = [
          {
            key: "details",
            label: (
              <Link
                to={`/admin/cash-field-officers/details/${record?.userId}?userName=${record?.user?.username}`}
              >
                <div className="flex items-center gap-2 justify-start text-slate-700 text-sm font-medium">
                  <EyeOutlined />
                  <span>Details</span>
                </div>
              </Link>
            ),
          },
        ];

        return (
          <div className="flex justify-center items-center gap-2">
            <Dropdown
              menu={{
                items,
                className: "min-w-[170px]",
              }}
              trigger={["click"]}
              placement="bottomRight"
              arrow={{ pointAtCenter: true }}
            >
              <Button
                className="action-button disabled:!bg-[#ddd] !border-none"
                size="small"
                icon={<ActionThreeDotIcon />}
              />
            </Dropdown>
          </div>
        );
      },
    },
  ];

  return (
    <>
      <SidebarLayout title="Field Officers">
        <CustomTable
          loading={
            getAllUserIsLoading || getAllUserIsFatching || statsByUsersIsLoading
          }
          limit={limit}
          page={page}
          columns={columns}
          total={getAllUserData?.data?.pagination?.total}
          dataSource={addKeyInArray(mergedUsers)}
          handleLimit={handleLimit}
          handlePagination={handlePagination}
          filterSection={
            <>
              <Input
                suffix={<SearchIcon />}
                placeholder="Search by Name"
                style={{ width: 215 }}
                allowClear
                onChange={handleSearch}
              />
            </>
          }
        />
      </SidebarLayout>
    </>
  );
}
